// apps/scheduler-worker/src/components/SerialNumberDisplay.tsx
import { QrCode, Copy, Check } from 'lucide-react';
import { useState } from 'react';

interface SerialNumberDisplayProps {
  serialNumber: string;
  partNumber?: string;
  compact?: boolean;
}

/**
 * Displays an assigned serial number prominently.
 * Includes copy-to-clipboard functionality.
 */
export function SerialNumberDisplay({
  serialNumber,
  partNumber,
  compact = false,
}: SerialNumberDisplayProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(serialNumber);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy serial number:', error);
    }
  };

  if (compact) {
    return (
      <div className="flex items-center gap-2 bg-purple-50 border border-purple-200 rounded-lg px-3 py-2">
        <QrCode className="w-4 h-4 text-purple-600" />
        <span className="font-mono font-semibold text-purple-800">{serialNumber}</span>
        <button
          onClick={handleCopy}
          className="ml-auto p-1 hover:bg-purple-100 rounded transition-colors"
          aria-label="Copy serial number"
        >
          {copied ? (
            <Check className="w-4 h-4 text-green-600" />
          ) : (
            <Copy className="w-4 h-4 text-purple-600" />
          )}
        </button>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border-2 border-purple-200 rounded-xl p-4">
      <div className="flex items-center gap-3 mb-2">
        <div className="bg-purple-100 rounded-full p-2">
          <QrCode className="w-6 h-6 text-purple-600" />
        </div>
        <div>
          <p className="text-xs text-purple-600 font-medium uppercase tracking-wider">
            Serial Number Assigned
          </p>
          {partNumber && (
            <p className="text-xs text-gray-500">{partNumber}</p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <p className="font-mono text-2xl font-bold text-purple-800 tracking-wide">
          {serialNumber}
        </p>
        <button
          onClick={handleCopy}
          className="p-2 hover:bg-purple-100 rounded-lg transition-colors"
          aria-label="Copy serial number"
        >
          {copied ? (
            <Check className="w-5 h-5 text-green-600" />
          ) : (
            <Copy className="w-5 h-5 text-purple-600" />
          )}
        </button>
      </div>
      {copied && (
        <p className="text-xs text-green-600 mt-1">Copied to clipboard!</p>
      )}
    </div>
  );
}
